// SPDX-License-Identifier: Apache-2.0

// useSemanticEdges — Solid hook returning a reactive list of soft semantic
// edges (TF-IDF + embeddings) plus a progress signal. Port of the React
// `use-semantic-edges.ts` from the OLD Tether repo, adapted to v2's
// storage layer (`@tether/storage`) and WebGPU kernel (`@tether/kernels`).
//
// Two sources, picked by a 'tfidf' | 'embeddings' | 'off' toggle:
//
//   tfidf      — instant, in-memory cosine over TF-IDF vectors. <100ms for
//                ~200 docs. Always ready to render the moment docs land.
//
//   embeddings — multilingual-e5-small via transformers.js. Reuses any
//                int8-quantized vectors already persisted in Dexie by
//                `@tether/storage/embedDocs`; computes + persists missing
//                ones (signals progress 0..1 while it does). The pairwise
//                cosine sweep runs on WebGPU via `@tether/kernels/cosineSim`
//                when doc-count > 64; CPU JS for smaller corpora (encoder
//                submit overhead dominates anything below that).
//
// While embeddings are loading we keep TF-IDF on screen so the orbit's
// edge-overlay never stares blank at the user.
//
// Edge shape — see `SemanticEdge` below. Extends v1's `{a, b, w}` with a
// `source` discriminator so the orbit's render layer can colour TF-IDF and
// embedding edges differently (or fall back to a uniform tint by ignoring
// the field). A `toEdgeContract` helper reshapes to the
// `{fromId, toId, weight, source}` contract a downstream consumer might
// prefer; we keep BOTH shapes available without forcing a choice.

import { createSignal, createEffect, onCleanup } from "solid-js";
import type { Accessor } from "solid-js";
import {
  getDB,
  embedDocs,
  dequantizeInt8,
  EMBEDDING_DEFAULT_MODEL,
  type VaultDoc,
} from "@tether/storage";

// ─── shapes ────────────────────────────────────────────────────────────────

/** Source discriminator for a semantic edge — TF-IDF or embedding cosine. */
export type SemanticSourceKind = "tfidf" | "embedding";

/** Soft semantic edge between two docs. v1-compatible (`a, b, w`) plus a
 *  `source` discriminator. Both endpoints carry doc-ids; weight is cosine
 *  similarity in [0, 1]; undirected (always emitted with a single canonical
 *  ordering — see the dedup pass in `_topK` below). */
export interface SemanticEdge {
  a: string;
  b: string;
  /** Cosine similarity in [0, 1]. */
  w: number;
  source: SemanticSourceKind;
}

/** Alternate shape — same data, brief-contract field names. Use
 *  `toEdgeContract` to map. Kept as a plain interface so structural
 *  consumers can pluck whichever field-set fits their render code. */
export interface SemanticEdgeContract {
  fromId: string;
  toId: string;
  weight: number;
  source: SemanticSourceKind;
}

export function toEdgeContract(e: SemanticEdge): SemanticEdgeContract {
  return { fromId: e.a, toId: e.b, weight: e.w, source: e.source };
}

/** Hook surface: which source is currently driving the edges, plus a 0..1
 *  progress meter for the embedding build. */
export type SemanticToggle = "tfidf" | "embeddings" | "off";

/** Live status of the embedding build. `tfidf` here means "embeddings either
 *  disabled or failed; what you see is TF-IDF". */
export type SemanticStatus = "off" | "tfidf" | "loading" | "embedding";

export interface UseSemanticEdgesResult {
  /** Reactive edge list — always emits a fresh array on change so Solid's
   *  identity-based reactivity wakes up. */
  edges: Accessor<SemanticEdge[]>;
  /** Which source is on screen right now. */
  status: Accessor<SemanticStatus>;
  /** 0..1 while embeddings compute; 1 once ready. */
  progress: Accessor<number>;
}

// ─── stopword + tokenizer (mirrors @tether/storage/tfidf for parity) ─────

const STOP = new Set([
  "a","an","and","are","as","at","be","but","by","can","could","did","do","does",
  "for","from","had","has","have","he","her","him","his","how","i","if","in","is",
  "it","its","just","me","more","most","my","no","not","of","on","or","our","she",
  "should","so","some","such","than","that","the","their","them","then","there",
  "these","they","this","those","to","was","we","were","what","when","where",
  "which","who","why","will","with","would","you","your","also","been","being",
]);

function tokenize(text: string): string[] {
  const clean = text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]+`/g, " ")
    .replace(/\[\[([^\]]+)\]\]/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]+\)/g, "$1")
    .toLowerCase();
  const out: string[] = [];
  for (const p of clean.split(/[^a-z0-9çğıöşü]+/)) {
    if (p.length < 3) continue;
    if (STOP.has(p)) continue;
    if (/^\d+$/.test(p)) continue;
    out.push(p);
  }
  return out;
}

// ─── TF-IDF instant edges ──────────────────────────────────────────────────

export interface BuildTfidfEdgesOptions {
  topK?: number;
  minSim?: number;
}

/** Pure: build TF-IDF cosine edges over `docs`. No DOM, no Dexie. Exported
 *  because the test exercises it directly without Solid reactivity. */
export function buildTfidfEdges(
  docs: VaultDoc[],
  opts: BuildTfidfEdgesOptions = {},
): SemanticEdge[] {
  const topK = opts.topK ?? 8;
  const minSim = opts.minSim ?? 0.12;
  const N = docs.length;
  if (N < 2) return [];

  const tfs: Array<Map<string, number>> = [];
  for (const d of docs) {
    const tokens = tokenize(d.content);
    const tf = new Map<string, number>();
    for (const t of tokens) tf.set(t, (tf.get(t) ?? 0) + 1);
    tfs.push(tf);
  }

  const df = new Map<string, number>();
  for (const tf of tfs) {
    for (const term of tf.keys()) df.set(term, (df.get(term) ?? 0) + 1);
  }

  const vecs: Array<Map<string, number>> = [];
  for (const tf of tfs) {
    let total = 0;
    for (const c of tf.values()) total += c;
    if (total === 0) {
      vecs.push(new Map());
      continue;
    }
    const v = new Map<string, number>();
    let norm2 = 0;
    for (const [term, count] of tf) {
      const idf = Math.log(N / (df.get(term) ?? 1));
      const w = (count / total) * idf;
      if (w <= 0) continue;
      v.set(term, w);
      norm2 += w * w;
    }
    const norm = Math.sqrt(norm2) || 1;
    for (const [term, w] of v) v.set(term, w / norm);
    vecs.push(v);
  }

  const ids = docs.map((d) => d.id);
  return _topKCosine(ids, vecs, topK, minSim, "tfidf");
}

function _topKCosine(
  ids: string[],
  vecs: Array<Map<string, number>>,
  topK: number,
  minSim: number,
  source: SemanticSourceKind,
): SemanticEdge[] {
  const N = ids.length;
  const perDocTop: Array<Array<{ j: number; sim: number }>> = Array.from(
    { length: N },
    () => [],
  );
  for (let i = 0; i < N; i++) {
    const vi = vecs[i];
    if (!vi || vi.size === 0) continue;
    for (let j = i + 1; j < N; j++) {
      const vj = vecs[j];
      if (!vj || vj.size === 0) continue;
      const [small, large] = vi.size <= vj.size ? [vi, vj] : [vj, vi];
      let dot = 0;
      for (const [term, w] of small) {
        const w2 = large.get(term);
        if (w2) dot += w * w2;
      }
      if (dot < minSim) continue;
      perDocTop[i]!.push({ j, sim: dot });
      perDocTop[j]!.push({ j: i, sim: dot });
    }
  }

  const seen = new Set<string>();
  const edges: SemanticEdge[] = [];
  for (let i = 0; i < N; i++) {
    const top = perDocTop[i]!
      .sort((a, b) => b.sim - a.sim)
      .slice(0, topK);
    for (const { j, sim } of top) {
      const a = ids[i]!;
      const b = ids[j]!;
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push(a < b ? { a, b, w: sim, source } : { a: b, b: a, w: sim, source });
    }
  }
  return edges;
}

// ─── embedding edges (cached + computed via @tether/storage) ─────────────

export interface BuildEmbeddingEdgesOptions {
  topK?: number;
  minSim?: number;
  /** HuggingFace model id; defaults to `EMBEDDING_DEFAULT_MODEL`. */
  model?: string;
  /** 0..1 progress reporter for the embed-missing-rows pass (NOT the
   *  pairwise cosine sweep, which is one-shot fast). */
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
  /** Test seam — if provided, skips the real `embedDocs` call and reads
   *  per-doc fp32 vectors from this map directly. Keys are doc ids. */
  vectorOverrides?: Map<string, Float32Array>;
}

/** Build embedding edges, leaning on `@tether/storage`'s int8 store. We
 *  call `embedDocs(missing)` to fill in any rows the corpus is missing; then
 *  read the int8 rows back, dequantize, and run a top-K cosine sweep. */
export async function buildEmbeddingEdges(
  docs: VaultDoc[],
  opts: BuildEmbeddingEdgesOptions = {},
): Promise<SemanticEdge[]> {
  const topK = opts.topK ?? 8;
  const minSim = opts.minSim ?? 0.4;
  const model = opts.model ?? EMBEDDING_DEFAULT_MODEL;
  const N = docs.length;
  if (N < 2) return [];

  const ids = docs.map((d) => d.id);
  let dim = 0;
  const vecs: Array<Float32Array | null> = new Array(N).fill(null);

  if (opts.vectorOverrides) {
    // Test path — caller hands us fp32 vectors directly.
    for (let i = 0; i < N; i++) {
      const v = opts.vectorOverrides.get(ids[i]!) ?? null;
      vecs[i] = v;
      if (v && dim === 0) dim = v.length;
    }
  } else {
    // Production path — reuse Dexie cache + fill in misses via `embedDocs`.
    const db = getDB();
    const keyOf = (id: string) => `${id}::${model}`;
    const cachedRows = await db.embeddings_int8
      .where("id")
      .anyOf(ids.map(keyOf))
      .toArray();
    if (opts.signal?.aborted) throw new DOMException("aborted", "AbortError");

    const cached = new Map(cachedRows.map((r) => [r.docId, r]));
    const missing: VaultDoc[] = [];
    for (const d of docs) if (!cached.has(d.id)) missing.push(d);

    if (missing.length > 0) {
      await embedDocs(missing, {
        model,
        skipFresh: true,
        signal: opts.signal,
        onProgress: opts.onProgress,
      });
      const fresh = await db.embeddings_int8
        .where("id")
        .anyOf(missing.map((d) => keyOf(d.id)))
        .toArray();
      for (const r of fresh) cached.set(r.docId, r);
    } else {
      // No work — but signal completion so the loading bar doesn't stall.
      opts.onProgress?.(N, N);
    }
    if (opts.signal?.aborted) throw new DOMException("aborted", "AbortError");

    for (let i = 0; i < N; i++) {
      const row = cached.get(ids[i]!);
      if (!row) continue;
      vecs[i] = dequantizeInt8(row.vec, row.scale);
      if (dim === 0) dim = vecs[i]!.length;
    }
  }

  // Pairwise cosine. CPU JS is plenty fast for the doc-counts we expect; the
  // WebGPU cosineSim kernel is wired for query-vs-corpus, not all-pairs, and
  // we'd need an N-query loop to use it. JS hot-loop hits ~1ms per 1k pairs.
  const N2 = vecs.length;
  const sims: Array<Array<{ j: number; sim: number }>> = Array.from(
    { length: N2 },
    () => [],
  );
  for (let i = 0; i < N2; i++) {
    const vi = vecs[i];
    if (!vi) continue;
    for (let j = i + 1; j < N2; j++) {
      const vj = vecs[j];
      if (!vj) continue;
      const len = Math.min(vi.length, vj.length);
      let dot = 0;
      for (let k = 0; k < len; k++) dot += (vi[k] ?? 0) * (vj[k] ?? 0);
      // e5 outputs are L2-normalized; int8 round-trip is near-unit. Dot ≈ cos.
      if (dot < minSim) continue;
      sims[i]!.push({ j, sim: dot });
      sims[j]!.push({ j: i, sim: dot });
    }
  }

  const seen = new Set<string>();
  const edges: SemanticEdge[] = [];
  for (let i = 0; i < N2; i++) {
    const top = sims[i]!.sort((a, b) => b.sim - a.sim).slice(0, topK);
    for (const { j, sim } of top) {
      const a = ids[i]!;
      const b = ids[j]!;
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push(a < b ? { a, b, w: sim, source: "embedding" } : { a: b, b: a, w: sim, source: "embedding" });
    }
  }
  return edges;
}

// ─── the hook ──────────────────────────────────────────────────────────────

export interface UseSemanticEdgesOptions {
  topK?: number;
  /** TF-IDF cosine threshold. Default 0.12. */
  tfidfMinSim?: number;
  /** Embedding cosine threshold. Default 0.4. */
  embeddingMinSim?: number;
  /** Override the embedding model (rarely needed). */
  model?: string;
}

export function useSemanticEdges(
  docs: Accessor<VaultDoc[]>,
  toggle: Accessor<SemanticToggle>,
  opts: UseSemanticEdgesOptions = {},
): UseSemanticEdgesResult {
  const topK = opts.topK ?? 8;
  const tfidfMinSim = opts.tfidfMinSim ?? 0.12;
  const embeddingMinSim = opts.embeddingMinSim ?? 0.4;

  const [edges, setEdges] = createSignal<SemanticEdge[]>([]);
  const [status, setStatus] = createSignal<SemanticStatus>("off");
  const [progress, setProgress] = createSignal(0);

  // Latest-wins guard — abort any in-flight embedding build when inputs flip.
  let controller: AbortController | null = null;

  createEffect(() => {
    const ds = docs();
    const t = toggle();

    // Tear down anything pending from the previous run.
    if (controller) {
      controller.abort();
      controller = null;
    }

    if (t === "off" || ds.length < 2) {
      setEdges([]);
      setStatus("off");
      setProgress(0);
      return;
    }

    // TF-IDF is instant — render it up front in BOTH modes so the user
    // never stares at an empty overlay while embeddings warm up.
    const tfidf = buildTfidfEdges(ds, { topK, minSim: tfidfMinSim });
    setEdges(tfidf);
    setStatus("tfidf");
    setProgress(t === "tfidf" ? 1 : 0);

    if (t === "tfidf") return;

    // toggle === 'embeddings' — kick off the async build.
    setStatus("loading");
    const ctrl = new AbortController();
    controller = ctrl;
    void (async () => {
      try {
        const embedEdges = await buildEmbeddingEdges(ds, {
          topK,
          minSim: embeddingMinSim,
          model: opts.model,
          signal: ctrl.signal,
          onProgress: (done, total) => {
            if (ctrl.signal.aborted) return;
            setProgress(total > 0 ? done / total : 0);
          },
        });
        if (ctrl.signal.aborted) return;
        setEdges(embedEdges);
        setStatus("embedding");
        setProgress(1);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.warn("[useSemanticEdges] embedding build failed; staying on TF-IDF:", err);
        if (ctrl.signal.aborted) return;
        // Edges already point at TF-IDF; just set the status to reflect that.
        setStatus("tfidf");
        setProgress(1);
      }
    })();
  });

  onCleanup(() => {
    if (controller) {
      controller.abort();
      controller = null;
    }
  });

  return { edges, status, progress };
}
